import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardMedia, Typography, Rating, ButtonGroup, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Box } from '@mui/material';
import { Map as MapIcon, Notes as NotesIcon, AccessTime as ClockIcon, CloseRounded as CloseIcon } from '@mui/icons-material';
import NotesDialog from './NotesDialog';
import { ReminderDialog } from './ReminderDialog';
import { toast } from 'react-toastify';
import { req } from '../utils';
const VenueCard = ({ venue, onMarkUseless }) => {
  //if venue.data is a string, parse it
  let data = typeof venue.data === 'string' ? JSON.parse(venue.data) : venue.data;
  //console.log("VenueCard", venue);
  //if venue.photos is a string, parse it
  let photos = typeof venue.photos === 'string' ? JSON.parse(venue.photos) : venue.photos;
  const [distance, setDistance] = useState(null);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [openReminderDialog, setOpenReminderDialog] = useState(false);
  const [openUselessDialog, setOpenUselessDialog] = useState(false);
  const [uselessNote, setUselessNote] = useState('');
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (data.geometry?.location && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const calculatedDistance = calculateDistance(
          position.coords.latitude,
          position.coords.longitude,
          data.geometry.location.lat,
          data.geometry.location.lng
        );
        setDistance(`${calculatedDistance.toFixed(1)} KM`);
      });
    }
  }, [data.geometry?.location]);

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Format opening hours if available
  const getOpeningHours = () => {
    if (data.opening_hours?.weekday_text) {
      const today = new Date().getDay();
      // Adjust index since Google's weekday_text starts with Sunday at 0
      return data.opening_hours.weekday_text[today];
    }
    return data.business_status === 'OPERATIONAL' ? 'Open' : 'Closed';
  };
  //console.log("final data", data);

  if (isHidden) {
    return null;
  }

  const handleMarkUseless = async () => {
    try {
      await req('POST', 'mark_lead_useless', {
        lead_id: venue.id,
        note: uselessNote
      });
      toast.success('Lead marked as useless');
      setOpenUselessDialog(false);
      setIsHidden(true);
    } catch (error) {
      console.error('Error marking lead as useless:', error);
      toast.error('Failed to mark lead as useless');
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {photos?.[0] && (
        <CardMedia
          component="img"
          height="200"
          image={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photo_reference=${photos[0].photo_reference}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
          alt={venue.name}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h5" component="h2">
          {venue.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {venue.vicinity}
        </Typography>
        {/* Add distance if available */}
        {distance && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {distance}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Rating value={Number(venue.rating)} precision={0.1} readOnly />
          <Typography variant="body2" color="text.secondary">
            ({venue.user_ratings_total} reviews)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {getOpeningHours()}
        </Typography>
        {/* Add notes if available */}
        {data.notes && (
          <Typography variant="body2" color="text.secondary">
            Notes: {data.notes}
          </Typography>
        )}
        
        {/* Action Buttons */}
        <ButtonGroup 
          variant="outlined" 
          fullWidth 
          color="persiangreen"
          size="small"
          sx={{ mt: 2 }}
        >
          <Button
            onClick={() => setOpenNotesDialog(true)}
            sx={{ flexDirection: 'column', padding: '6px 8px' }}
          >
            <NotesIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              Notes
            </Typography>
          </Button>
          <Button
            onClick={() => setOpenReminderDialog(true)}
            sx={{ flexDirection: 'column', padding: '6px 8px' }}
          >
            <ClockIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              Reminder
            </Typography>
          </Button>
          <Button
            component="a"
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(venue.vicinity)}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flexDirection: 'column', padding: '6px 8px' }}
          >
            <MapIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              Directions
            </Typography>
          </Button>
          <Button
            color="persiangreen"
            onClick={() => setOpenUselessDialog(true)}
            sx={{ flexDirection: 'column', padding: '6px 8px' }}
          >
            <CloseIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              Useless
            </Typography>
          </Button>
        </ButtonGroup>
      </CardContent>
      <NotesDialog
        open={openNotesDialog}
        onClose={() => setOpenNotesDialog(false)}
        accountId={venue.id}
        venueId={venue.id}
        type="lead"
      />
      <ReminderDialog
        open={openReminderDialog}
        onClose={() => setOpenReminderDialog(false)}
        businessName={venue.name}
        venueId={venue.id}
        onSubmit={async (reminderData) => {
          try {
            console.log("reminderData", venue);
            await req('POST', 'save_reminder', {
              date: reminderData.reminderDate,
              note: reminderData.note,
              business_id: venue.id,
              type: 'lead'
            });
            toast.success('Reminder set successfully');
          } catch (error) {
            console.error('Error saving reminder:', error);
            toast.error('Failed to set reminder');
          }
        }}
      />
      <Dialog open={openUselessDialog} onClose={() => setOpenUselessDialog(false)}>
        <DialogTitle>Why is this lead useless?</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
          
            multiline
            rows={4}
            value={uselessNote}
            onChange={(e) => setUselessNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUselessDialog(false)}>Cancel</Button>
          <Button onClick={handleMarkUseless} color="persiangreen">
            Mark as Useless
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default VenueCard;
